import { Graphics } from 'pixi.js';
import { SHIPS, ShipClass, WARP_CONFIG, WarpPhase, ENTITY_RADII, getShipCollisionShapes, getShipBoundingRadius, type EntityType, type SelectionInfo, type CollisionCircle } from '@astra/shared';
import type { RemotePlayerData } from '../systems/NetworkManager';
import { ShipGraphics, DEFAULT_SHIP_COLOR, SVG_SHIP_META, SHIP_ID_TO_DESIGN, SIZE_SCALE_MULTIPLIERS, createShieldShatter } from './ShipGraphics';
import { ShipEntity } from './base/ShipEntity';
import { degToRad, radToDeg } from '../utils/angleUtils';
import { tween } from '../core/TweenManager';

export class RemotePlayer extends ShipEntity {
  private shipGraphics: ShipGraphics;

  // Abstract properties from SpaceEntity/TargetableEntity/CollidableEntity
  public readonly entityType: EntityType = 'remotePlayer';
  public collisionRadius: number = ENTITY_RADII.PLAYER_SHIP;
  public collisionShapes: CollisionCircle[] = [];
  public readonly isHostile: boolean = true; // Other players are always potential hostiles
  public mass: number = 100;
  public readonly selectionRadius: number = 40;

  // Use sessionId as the id
  public sessionId: string;
  public get id(): string {
    return this.sessionId;
  }

  // Ship properties (implementing ShipEntity abstracts)
  private _visibleShipClassId: string;
  private _shipColor: number = DEFAULT_SHIP_COLOR;

  public get visibleShipClassId(): string {
    return this._visibleShipClassId;
  }

  public get shipColor(): number {
    return this._shipColor;
  }

  public shipClass: ShipClass;
  public username: string;
  public corpTag: string = '';
  public totalPower: number = 0;

  // Docking state
  public isDocked: boolean = false;
  public isDockingAnimation: boolean = false;
  public dockedStationId: string = '';
  public dockPosition: number = 0;

  // Planet management state
  public isManagingPlanet: boolean = false;

  // Warp travel state
  public warpPhase: WarpPhase = 'none';
  public warpTargetX: number = 0;
  public warpTargetY: number = 0;
  public warpSpeed: number = 0;
  private warpTrailGraphics?: Graphics;

  // Combat-overhaul: natural shield state
  public shieldActive: boolean = false;
  public shieldRadius: number = 80;
  public shieldRaisedAt: number = 0;
  public shieldExpansionMs: number = 300;
  public isOverheated: boolean = false;
  public isVenting: boolean = false;
  private _shieldBubbleGraphics?: Graphics;

  // Boost state
  public isBoosting: boolean = false;

  // DEV: dummy enemy flag
  public isDummy: boolean = false;

  // Beam weapon state
  public beamFiring: boolean = false;
  private _remoteBeamGraphics?: Graphics;
  private _remoteBeamPulseTime: number = 0;

  // Overload visual state
  private _overloadPhase: number = 0;
  private _wasOverloaded: boolean = false;

  // Venting effect particles
  private _ventParticles: Array<{
    angle: number; dist: number; speed: number; size: number;
    alpha: number; drift: number; life: number; maxLife: number;
  }> = [];
  private _ventTime: number = 0;

  // Warp calculation absorption particles
  private _calcAbsorptionParticles: Array<{
    angle: number; dist: number; startDist: number;
    speed: number; size: number; color: number; trail: number;
  }> = [];
  private _calcAbsorptionTime: number = 0;

  // Thrusting state for visual
  private _thrusting: boolean = false;

  constructor(data: RemotePlayerData) {
    super();

    // Enable entity interpolation buffer for smooth remote player rendering (Phase 2 networking)
    this.useInterpolationBuffer = true;

    this.position.set(data.x, data.y);
    this.targetX = data.x;
    this.targetY = data.y;

    this.sessionId = data.sessionId;
    this._visibleShipClassId = data.shipClassId;
    this.shipClass = SHIPS[data.shipClassId] || SHIPS.sparrow;
    this.collisionShapes = getShipCollisionShapes(this.shipClass.class, this.shipClass.size);
    this.collisionRadius = getShipBoundingRadius(this.shipClass.class, this.shipClass.size);
    // Scale stun circle to match ship size
    const rDesignKey = SHIP_ID_TO_DESIGN[data.shipClassId] || data.shipClassId;
    const rSvgMeta = SVG_SHIP_META[rDesignKey];
    const rSizeMult = SIZE_SCALE_MULTIPLIERS[this.shipClass.size || 'medium'] || 1.0;
    this.stunIndicatorRadius = rSvgMeta
      ? rSvgMeta.thrustOffsetY * rSvgMeta.scale * rSizeMult + 10
      : 35;
    this.mass = this.shipClass.stats.mass;
    this.username = data.username;
    this.corpTag = data.corpTag ?? '';
    this.isDummy = data.isDummy ?? false;
    this.totalPower = data.totalPower;
    this.hull = data.hull;
    this.maxHull = data.maxHull;

    // Set ship color if provided
    if (data.shipColor !== undefined) {
      this._shipColor = data.shipColor;
    }

    // Create procedural ship graphics with color and size
    this.shipGraphics = new ShipGraphics(0, 0, data.shipClassId, {
      color: this._shipColor,
      size: this.shipClass.size,
      tier: this.shipClass.tier,
    });
    this.addChild(this.shipGraphics);

    // Generate thrust textures for remote players
    this.shipGraphics.generateThrustTextures();

    // Create name tag (from ShipEntity) — members show their corp tag, e.g. "[ACME] Pilot".
    this.createNameTag(this.corpTag ? `[${this.corpTag}] ${data.username}` : data.username, 0xffffff);

    // Create damage indicator (from ShipEntity)
    this.createDamageIndicator();

    // Create warp trail graphics - NOT added to this container
    this.warpTrailGraphics = new Graphics();
    this.warpTrailGraphics.zIndex = 4;

    // Initial state
    this.updateFromServer(data);
  }

  // Get warp trail graphics for parent to add to scene
  public getWarpTrailGraphics(): Graphics | undefined {
    return this.warpTrailGraphics;
  }

  updateFromServer(data: Partial<RemotePlayerData>): void {
    // Use syncFromServer for position/velocity updates
    if (data.x !== undefined || data.y !== undefined || data.velocityX !== undefined || data.velocityY !== undefined) {
      this.syncFromServer(
        data.x ?? this.targetX,
        data.y ?? this.targetY,
        data.velocityX,
        data.velocityY
      );
    }

    if (data.rotation !== undefined) this.targetRotation = data.rotation;
    if (data.hull !== undefined) this.hull = data.hull;
    if (data.maxHull !== undefined) this.maxHull = data.maxHull;
    if (data.shield !== undefined) this.shield = data.shield;
    if (data.maxShield !== undefined) this.maxShield = data.maxShield;

    // Stun state from server (overrides local collision stun)
    if (data.isStunned !== undefined) {
      this.isStunned = data.isStunned;
      if (data.stunEndTime !== undefined) this.stunEndTime = data.stunEndTime;
    }

    // Combat-overhaul: natural shield state
    if (data.isOverheated !== undefined) this.isOverheated = data.isOverheated;
    if (data.isVenting !== undefined) this.isVenting = data.isVenting;
    if (data.shieldActive !== undefined) {
      const wasActive = this.shieldActive;
      this.shieldActive = data.shieldActive;
      // Shatter only on overload (not manual lower)
      if (wasActive && !data.shieldActive && this.isOverheated && this.parent) {
        const coverageDeg = this.shipClass?.stats?.shieldCoverage ?? 360;
        const noseAngle = this.rotation - Math.PI / 2;
        createShieldShatter(this.parent, this.x, this.y, this.shieldRadius, coverageDeg, noseAngle);
      }
    }
    if (data.shieldRadius !== undefined) this.shieldRadius = data.shieldRadius;
    if (data.shieldRaisedAt !== undefined) this.shieldRaisedAt = data.shieldRaisedAt;
    if (data.shieldExpansionMs !== undefined) this.shieldExpansionMs = data.shieldExpansionMs;

    if (data.thrusting !== undefined) {
      this.shipGraphics.thrusting = data.thrusting;
      this._thrusting = data.thrusting;
    }

    // Boost state — intensify engine flare
    if (data.isBoosting !== undefined) {
      this.isBoosting = data.isBoosting;
      this.shipGraphics.setFlareIntensity(data.isBoosting ? 1.3 : 1.0);
    }

    // Beam weapon state
    if (data.beamFiring !== undefined) {
      this.beamFiring = data.beamFiring;
      if (!data.beamFiring && this._remoteBeamGraphics) {
        this._remoteBeamGraphics.clear();
      }
    }

    if (data.username !== undefined) this.username = data.username;
    if (data.corpTag !== undefined) this.corpTag = data.corpTag;
    if (data.username !== undefined || data.corpTag !== undefined) {
      this.nameTag.text = this.corpTag ? `[${this.corpTag}] ${this.username}` : this.username;
    }

    // Update ship class if changed (e.g. dev panel ship swap in-flight)
    if (data.shipClassId !== undefined && data.shipClassId !== this._visibleShipClassId) {
      const newShipClass = SHIPS[data.shipClassId];
      if (newShipClass) {
        this._visibleShipClassId = data.shipClassId;
        this.shipClass = newShipClass;
        this.mass = newShipClass.stats.mass;
        this.collisionShapes = getShipCollisionShapes(newShipClass.class, newShipClass.size);
        this.collisionRadius = getShipBoundingRadius(newShipClass.class, newShipClass.size);

        // Recalculate stun indicator radius from SVG metadata
        const designKey = SHIP_ID_TO_DESIGN[data.shipClassId] || data.shipClassId;
        const svgMeta = SVG_SHIP_META[designKey];
        const sizeMult = SIZE_SCALE_MULTIPLIERS[newShipClass.size || 'medium'] || 1.0;
        this.stunIndicatorRadius = svgMeta
          ? svgMeta.thrustOffsetY * svgMeta.scale * sizeMult + 10
          : 35;

        // Recreate ship graphics
        this.removeChild(this.shipGraphics);
        this.shipGraphics.destroy();
        this.shipGraphics = new ShipGraphics(0, 0, data.shipClassId, {
          color: this._shipColor,
          size: newShipClass.size,
          tier: newShipClass.tier,
        });
        this.addChild(this.shipGraphics);
        this.shipGraphics.generateThrustTextures();
        if (this._thrusting) this.shipGraphics.thrusting = true;
      }
    }

    // Update ship color if changed
    if (data.shipColor !== undefined && data.shipColor !== this._shipColor) {
      this._shipColor = data.shipColor;
      this.shipGraphics.shipColor = data.shipColor;
    }

    // Update docking state
    if (data.isDocked !== undefined) {
      const wasDocked = this.isDocked;
      this.isDocked = data.isDocked;

      // Hide player when docked (they're inside the station)
      if (data.isDocked && !wasDocked) {
        // Player just docked - hide them
        this.visible = false;
        this.nameTag.visible = false;
        if (this.damageIndicator) this.damageIndicator.visible = false;
      } else if (!data.isDocked && wasDocked) {
        // Player just undocked - show them
        this.visible = true;
        this.nameTag.visible = true;
      }
    }
    if (data.isDockingAnimation !== undefined) this.isDockingAnimation = data.isDockingAnimation;
    if (data.dockedStationId !== undefined) this.dockedStationId = data.dockedStationId;
    if (data.dockPosition !== undefined) this.dockPosition = data.dockPosition;

    // Update planet management state — hide ship while managing a planet
    if (data.isManagingPlanet !== undefined) {
      const wasManaging = this.isManagingPlanet;
      this.isManagingPlanet = data.isManagingPlanet;

      if (data.isManagingPlanet && !wasManaging) {
        this.visible = false;
        this.nameTag.visible = false;
        if (this.damageIndicator) this.damageIndicator.visible = false;
      } else if (!data.isManagingPlanet && wasManaging) {
        this.visible = true;
        this.nameTag.visible = true;
      }
    }

    // Update warp travel state
    if (data.warpPhase !== undefined) this.warpPhase = data.warpPhase as WarpPhase;
    if (data.warpTargetX !== undefined) this.warpTargetX = data.warpTargetX;
    if (data.warpTargetY !== undefined) this.warpTargetY = data.warpTargetY;
    if (data.warpSpeed !== undefined) this.warpSpeed = data.warpSpeed;

    // Check for death
    if (this.hull <= 0 && !this.isDestroyed) {
      this.explode();
    }
  }

  update(delta: number): void {
    // Remote player stun is server-authoritative — just render the visual, don't use timer-based clearing
    this.updateStunVisual();

    if (this.isDestroyed) {
      // Update debris (from ShipEntity)
      this.updateDebris(delta);
      return;
    }

    // Skip update entirely when docked or managing a planet - player is hidden
    if (this.isDocked || this.isManagingPlanet) {
      return;
    }

    // Warp calculating absorption particles (visible to other players)
    if (this.warpPhase === 'calculating') {
      this.updateWarpCalculationEffect();
    } else if (this._calcAbsorptionParticles.length > 0) {
      this._calcAbsorptionParticles = [];
      this._calcAbsorptionTime = 0;
    }

    // Handle warp traveling separately (no interpolation, direct position updates)
    if (this.warpPhase === 'traveling') {
      // Set position directly from server target (no interpolation for warp)
      this.x = this.targetX;
      this.y = this.targetY;

      // Face the warp target
      const dx = this.warpTargetX - this.x;
      const dy = this.warpTargetY - this.y;
      if (dx !== 0 || dy !== 0) {
        this._angle = radToDeg(Math.atan2(dy, dx)) + 90;
        this.rotation = degToRad(this._angle);
      }

      // Show thrust during warp
      this.shipGraphics.thrusting = true;

      this.updateNameAndIndicatorPositions();
      this.updateWarpTrailEffect();
      this.shipGraphics.update();
      return;
    }

    // Clear warp trail when not in warp
    if (this.warpTrailGraphics) {
      this.warpTrailGraphics.clear();
    }

    // Use base class position interpolation
    this.updatePosition(delta, 0.15);

    // Use ShipEntity rotation interpolation (frame-rate independent)
    this.updateRotationInterpolation(0.15, delta);

    // Update name tag and damage indicator positions (from ShipEntity)
    this.updateNameAndIndicatorPositions();

    // Update ship graphics
    this.shipGraphics.update();

    // Combat-overhaul: render natural shield bubble
    this.updateShieldBubble();

    // Overload tint effect (pulsing red/orange/yellow on hull)
    this.updateOverloadVisual(delta);

    // Venting heat particles
    this.updateVentingEffect(delta);

    // Beam weapon visual (red laser from nose in facing direction)
    this.updateRemoteBeam(delta);
  }

  private updateShieldBubble(): void {
    if (!this.shieldActive) {
      if (this._shieldBubbleGraphics) this._shieldBubbleGraphics.clear();
      return;
    }
    if (!this._shieldBubbleGraphics) {
      this._shieldBubbleGraphics = new Graphics();
      this._shieldBubbleGraphics.zIndex = 18;
      if (this.parent) this.parent.addChild(this._shieldBubbleGraphics);
    }
    const sinceRaise = this.shieldRaisedAt ? Date.now() - this.shieldRaisedAt : this.shieldExpansionMs;
    const t = Math.min(1, sinceRaise / Math.max(1, this.shieldExpansionMs));
    const currentRadius = this.shieldRadius * t;
    if (currentRadius < 4) { this._shieldBubbleGraphics.clear(); return; }

    const g = this._shieldBubbleGraphics;
    g.clear();

    const coverageDeg = this.shipClass?.stats?.shieldCoverage ?? 360;
    if (coverageDeg >= 360) {
      g.circle(this.x, this.y, currentRadius);
      g.fill({ color: 0x66ccff, alpha: 0.06 });
      g.circle(this.x, this.y, currentRadius);
      g.stroke({ width: 1.5, color: 0x88ddff, alpha: 0.45 });
    } else {
      // Partial arc facing ship nose (0° = up → nose = rotation - π/2)
      const noseAngle = this.rotation - Math.PI / 2;
      const halfArc = (coverageDeg / 2) * (Math.PI / 180);
      const startAngle = noseAngle - halfArc;
      const endAngle = noseAngle + halfArc;

      g.moveTo(this.x, this.y);
      g.arc(this.x, this.y, currentRadius, startAngle, endAngle);
      g.closePath();
      g.fill({ color: 0x66ccff, alpha: 0.06 });

      g.arc(this.x, this.y, currentRadius, startAngle, endAngle);
      g.stroke({ width: 1.5, color: 0x88ddff, alpha: 0.45 });
    }
  }

  /** Overload tint: cycle red → orange → yellow on ship hull while overheated. */
  private updateOverloadVisual(delta: number): void {
    const overloaded = this.isOverheated;
    if (!overloaded) {
      if (this._wasOverloaded) {
        this.shipGraphics.setOverloadTint(0xffffff);
        this._wasOverloaded = false;
      }
      return;
    }
    this._wasOverloaded = true;
    this._overloadPhase += delta * 0.006;
    const cycle = (this._overloadPhase * 1.5) % 3;
    let r: number, gCh: number, b: number;
    if (cycle < 1) {
      const t = cycle;
      r = 0xff; gCh = Math.round(0x22 + (0x88 - 0x22) * t); b = 0x00;
    } else if (cycle < 2) {
      const t = cycle - 1;
      r = 0xff; gCh = Math.round(0x88 + (0xdd - 0x88) * t); b = Math.round(0x22 * t);
    } else {
      const t = cycle - 2;
      r = 0xff; gCh = Math.round(0xdd - (0xdd - 0x22) * t); b = Math.round(0x22 * (1 - t));
    }
    this.shipGraphics.setOverloadTint((r << 16) | (gCh << 8) | b);
  }

  /** Venting heat particles: white wisps radiating outward from ship. */
  private updateVentingEffect(delta: number): void {
    const active = this.isVenting || this.isOverheated;
    if (!active) {
      if (this._ventParticles.length > 0) {
        this._ventParticles = [];
        this._ventTime = 0;
      }
      return;
    }

    // Lazily create graphics if needed (reuse shield bubble layer)
    if (!this._shieldBubbleGraphics) {
      this._shieldBubbleGraphics = new Graphics();
      this._shieldBubbleGraphics.zIndex = 18;
      if (this.parent) this.parent.addChild(this._shieldBubbleGraphics);
    }
    const g = this._shieldBubbleGraphics;
    // Shield bubble already cleared in updateShieldBubble (shield is down during venting)

    this._ventTime += delta * 0.004;
    const t = this._ventTime;

    const maxParticles = 40;
    const spawnRadius = 15;
    const maxRadius = 140;

    while (this._ventParticles.length < maxParticles) {
      const angle = Math.random() * Math.PI * 2;
      this._ventParticles.push({
        angle, dist: spawnRadius + Math.random() * 10,
        speed: 0.6 + Math.random() * 1.2, size: 0.6 + Math.random() * 1.8,
        alpha: 0.5 + Math.random() * 0.4, drift: (Math.random() - 0.5) * 0.015,
        life: 0, maxLife: 0.8 + Math.random() * 0.4,
      });
    }

    for (let i = this._ventParticles.length - 1; i >= 0; i--) {
      const p = this._ventParticles[i];
      p.dist += p.speed;
      p.life += 0.012;
      p.angle += p.drift + Math.sin(t * 3 + p.angle * 5) * 0.004;
      p.speed *= 0.995;

      if (p.dist > maxRadius || p.life > p.maxLife) {
        const angle = Math.random() * Math.PI * 2;
        p.angle = angle; p.dist = spawnRadius + Math.random() * 10;
        p.speed = 0.6 + Math.random() * 1.2; p.size = 0.6 + Math.random() * 1.8;
        p.alpha = 0.5 + Math.random() * 0.4; p.drift = (Math.random() - 0.5) * 0.015;
        p.life = 0; p.maxLife = 0.8 + Math.random() * 0.4;
        continue;
      }

      const px = this.x + Math.cos(p.angle) * p.dist;
      const py = this.y + Math.sin(p.angle) * p.dist;

      const normalizedDist = (p.dist - spawnRadius) / (maxRadius - spawnRadius);
      const distFade = 1 - normalizedDist;
      const lifeFade = p.life < 0.15
        ? p.life / 0.15
        : p.life > p.maxLife * 0.7
          ? (p.maxLife - p.life) / (p.maxLife * 0.3)
          : 1;
      const alpha = p.alpha * distFade * lifeFade * 0.7;
      if (alpha <= 0.01) continue;

      const trailLen = 6 + p.speed * 4;
      const trailX = px - Math.cos(p.angle) * trailLen;
      const trailY = py - Math.sin(p.angle) * trailLen;
      g.moveTo(trailX, trailY);
      g.lineTo(px, py);
      g.stroke({ width: p.size * 0.5, color: 0xffffff, alpha: alpha * 0.3 });

      const pSize = p.size + Math.sin(t * 6 + p.angle * 4) * 0.3;
      g.circle(px, py, pSize);
      g.fill({ color: 0xffffff, alpha: alpha * 0.9 });

      if (p.size > 1.2) {
        g.circle(px, py, pSize * 1.8);
        g.fill({ color: 0xffddaa, alpha: alpha * 0.15 });
      }
    }

    const hazeR = 20 + Math.sin(t * 5) * 4;
    g.circle(this.x, this.y, hazeR + 10);
    g.fill({ color: 0xffeecc, alpha: 0.02 + Math.sin(t * 7) * 0.01 });
    g.circle(this.x, this.y, hazeR);
    g.fill({ color: 0xffffff, alpha: 0.04 + Math.sin(t * 9) * 0.015 });
  }

  /** Get nose offset (distance from center to front of ship) for beam start position. */
  private getNoseOffset(): number {
    const designKey = SHIP_ID_TO_DESIGN[this._visibleShipClassId] || this._visibleShipClassId;
    const svgMeta = SVG_SHIP_META[designKey];
    if (svgMeta) {
      const sizeMultiplier = SIZE_SCALE_MULTIPLIERS[this.shipClass?.size || 'medium'] || 1.0;
      return svgMeta.thrustOffsetY * svgMeta.scale * sizeMultiplier;
    }
    return 25;
  }

  /** Draw a visual-only combat laser beam from nose in facing direction. */
  private updateRemoteBeam(delta: number): void {
    if (!this.beamFiring) {
      if (this._remoteBeamGraphics) this._remoteBeamGraphics.clear();
      return;
    }
    if (!this._remoteBeamGraphics) {
      this._remoteBeamGraphics = new Graphics();
      this._remoteBeamGraphics.zIndex = 15;
      if (this.parent) this.parent.addChild(this._remoteBeamGraphics);
    }
    this._remoteBeamPulseTime += delta / 1000;
    const g = this._remoteBeamGraphics;
    g.clear();

    // Beam starts at ship nose
    const noseAngle = this.rotation - Math.PI / 2;
    const noseOffset = this.getNoseOffset();
    const startX = this.x + Math.cos(noseAngle) * noseOffset;
    const startY = this.y + Math.sin(noseAngle) * noseOffset;

    // Beam extends in facing direction to default range (500 units)
    const range = 500;
    const endX = startX + Math.cos(noseAngle) * range;
    const endY = startY + Math.sin(noseAngle) * range;

    // Pulsing width
    const pulse = Math.sin(this._remoteBeamPulseTime * 8) * 0.3 + 0.7;
    const baseWidth = 2.5;
    const currentWidth = baseWidth * pulse;

    // Glow (outer)
    g.moveTo(startX, startY);
    g.lineTo(endX, endY);
    g.stroke({ width: currentWidth + 6, color: 0xff4444, alpha: 0.2 });

    // Main beam
    g.moveTo(startX, startY);
    g.lineTo(endX, endY);
    g.stroke({ width: currentWidth + 2, color: 0xff0000, alpha: 0.8 });

    // Bright core
    g.moveTo(startX, startY);
    g.lineTo(endX, endY);
    g.stroke({ width: 1, color: 0xffffff, alpha: 0.6 });
  }

  /** Warp calculating absorption particles — converge from radius toward ship center. */
  private updateWarpCalculationEffect(): void {
    if (!this.warpTrailGraphics) return;

    const g = this.warpTrailGraphics;
    this._calcAbsorptionTime += 0.04;
    const t = this._calcAbsorptionTime;

    const calcPalette = [0x00aaff, 0x00ccff, 0x20a5a6, 0x8855ff, 0xaa44ff, 0x00ddff, 0x4488ff, 0xffffff];
    const pickColor = () => calcPalette[Math.floor(Math.random() * calcPalette.length)];

    const maxRadius = 140;
    const maxParticles = 30; // Lighter than local player (50)

    while (this._calcAbsorptionParticles.length < maxParticles) {
      const angle = Math.random() * Math.PI * 2;
      const startDist = maxRadius * (0.6 + Math.random() * 0.4);
      this._calcAbsorptionParticles.push({
        angle, dist: startDist, startDist,
        speed: 0.8 + Math.random() * 1.5,
        size: 0.8 + Math.random() * 2.0,
        color: pickColor(),
        trail: 0.3 + Math.random() * 0.7,
      });
    }

    for (let i = this._calcAbsorptionParticles.length - 1; i >= 0; i--) {
      const p = this._calcAbsorptionParticles[i];
      p.dist -= p.speed;
      p.angle += 0.008 + (1 - p.dist / p.startDist) * 0.02;
      if (p.dist < maxRadius * 0.4) p.speed *= 1.02;

      if (p.dist <= 4) {
        const angle = Math.random() * Math.PI * 2;
        const startDist = maxRadius * (0.6 + Math.random() * 0.4);
        p.angle = angle; p.dist = startDist; p.startDist = startDist;
        p.speed = 0.8 + Math.random() * 1.5;
        p.size = 0.8 + Math.random() * 2.0;
        p.color = pickColor();
        p.trail = 0.3 + Math.random() * 0.7;
        continue;
      }

      const px = this.x + Math.cos(p.angle) * p.dist;
      const py = this.y + Math.sin(p.angle) * p.dist;
      const normalizedDist = p.dist / maxRadius;
      const alpha = Math.min(0.9, 0.15 + (1 - normalizedDist) * 0.75);

      const trailLen = p.trail * 12 * (1 - normalizedDist + 0.3);
      const trailX = px + Math.cos(p.angle) * trailLen;
      const trailY = py + Math.sin(p.angle) * trailLen;
      g.moveTo(trailX, trailY);
      g.lineTo(px, py);
      g.stroke({ width: p.size * 0.6, color: p.color, alpha: alpha * 0.4 });

      const pSize = p.size + Math.sin(t * 8 + p.angle * 3) * 0.4;
      g.circle(px, py, pSize);
      g.fill({ color: p.color, alpha });

      if (p.size > 1.5) {
        g.circle(px, py, pSize * 0.5);
        g.fill({ color: 0xffffff, alpha: alpha * 0.8 });
      }
    }

    // Central glow
    const glowR = 6 + Math.sin(t * 6) * 2;
    g.circle(this.x, this.y, glowR + 10);
    g.fill({ color: 0x00aaff, alpha: 0.03 + Math.sin(t * 4) * 0.015 });
    g.circle(this.x, this.y, glowR + 5);
    g.fill({ color: 0x20a5a6, alpha: 0.06 + Math.sin(t * 5) * 0.02 });
    g.circle(this.x, this.y, glowR + 2);
    g.fill({ color: 0x00ccff, alpha: 0.15 + Math.sin(t * 7) * 0.05 });
    g.circle(this.x, this.y, glowR);
    g.fill({ color: 0xffffff, alpha: 0.4 + Math.sin(t * 8) * 0.1 });
  }

  // Override onDestroy hook from DamageableEntity
  protected override onDestroy(): void {
    // Hide ship graphics, name tag, and damage indicator
    this.shipGraphics.setVisible(false);
    this.nameTag.visible = false;
    if (this.damageIndicator) this.damageIndicator.visible = false;

    // Create explosion debris (from ShipEntity)
    this.createExplosionDebris();
  }

  // Disable the ship (Red zone death - ship not destroyed, but disabled)
  public disable(): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    // Dim ship graphics to show disabled state
    this.shipGraphics.setAlpha(0.3);
    this.nameTag.visible = false;
    if (this.damageIndicator) this.damageIndicator.visible = false;

    // Create sparks/smoke effect
    this.createDisableEffect();
  }

  // Create visual effect for disabled ship
  private createDisableEffect(): void {
    if (!this.parent) return;

    // Create smoke particles
    const smokeColors = [0x444444, 0x666666, 0x888888];
    for (let i = 0; i < 6; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 20 + Math.random() * 30;
      const gfx = new Graphics();
      const color = smokeColors[Math.floor(Math.random() * smokeColors.length)];
      gfx.circle(0, 0, 3 + Math.random() * 4);
      gfx.fill({ color, alpha: 0.7 });
      gfx.position.set(this.x, this.y);
      this.parent.addChild(gfx);

      const targetX = this.x + Math.cos(angle) * speed;
      const targetY = this.y + Math.sin(angle) * speed - 30;
      tween({
        targets: gfx,
        x: targetX,
        y: targetY,
        alpha: 0,
        duration: 1500 + Math.random() * 500,
        onComplete: () => gfx.destroy(),
      });
    }

    // Create spark particles
    const sparkColors = [0xffff00, 0xffaa00, 0xff6600];
    for (let i = 0; i < 5; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 30 + Math.random() * 50;
      const gfx = new Graphics();
      const color = sparkColors[Math.floor(Math.random() * sparkColors.length)];
      gfx.circle(0, 0, 1 + Math.random() * 2);
      gfx.fill({ color, alpha: 1 });
      gfx.position.set(this.x, this.y);
      this.parent.addChild(gfx);

      const targetX = this.x + Math.cos(angle) * speed;
      const targetY = this.y + Math.sin(angle) * speed;
      tween({
        targets: gfx,
        x: targetX,
        y: targetY,
        alpha: 0,
        duration: 500 + Math.random() * 300,
        onComplete: () => gfx.destroy(),
      });
    }
  }

  // Override checkHit to also check docking state
  public override checkHit(x: number, y: number): boolean {
    if (this.isDocked || this.isManagingPlanet) return false;
    return super.checkHit(x, y);
  }

  // Respawn (reset state if player comes back)
  public respawn(): void {
    this.isDestroyed = false;

    // Clear debris
    this.debris.forEach(d => d.destroy());
    this.debris = [];

    // Show ship graphics and name tag (damage indicator stays hidden until hit)
    this.shipGraphics.setVisible(true);
    this.shipGraphics.setAlpha(1);
    this.nameTag.visible = true;
  }

  // Warp trail animation state (simplified parallel-lines for remote ships)
  private _remoteWarpTime: number = 0;
  private _remoteWarpLines: Array<{ pos: number; lateral: number; speed: number; len: number; width: number }> = [];

  // Update warp trail — parallel speed-lines + core trail (lighter version of Player)
  private updateWarpTrailEffect(): void {
    if (!this.warpTrailGraphics) return;

    if (this.warpPhase !== 'traveling') {
      this.warpTrailGraphics.clear();
      this._remoteWarpLines = [];
      this._remoteWarpTime = 0;
      return;
    }

    this.warpTrailGraphics.clear();
    this._remoteWarpTime += 0.06;

    const speed = Math.sqrt(this.velocityX * this.velocityX + this.velocityY * this.velocityY);
    if (speed < 100) return;

    const g = this.warpTrailGraphics;
    const velAngle = Math.atan2(this.velocityY, this.velocityX);
    const cosV = Math.cos(velAngle);
    const sinV = Math.sin(velAngle);
    const cosP = Math.cos(velAngle + Math.PI / 2);
    const sinP = Math.sin(velAngle + Math.PI / 2);
    const warpColor = WARP_CONFIG.WARP_COLOR;
    const t = this._remoteWarpTime;

    const ahead = Math.min(speed * 0.3, 250);
    const behind = Math.min(speed * 0.8, 600);
    const totalRange = ahead + behind;
    const spreadRadius = 60;

    // Core trail behind ship (3 layers)
    const coreSegs = 8;
    for (let layer = 0; layer < 3; layer++) {
      const frac = layer / 2;
      const w = 30 * (1 - frac * 0.9);
      const a = 0.03 + frac * 0.1;
      const col = frac > 0.8 ? 0xffffff : warpColor;
      for (let s = 0; s < coreSegs; s++) {
        const r0 = s / coreSegs;
        const r1 = (s + 1) / coreSegs;
        g.moveTo(this.x - cosV * behind * r0, this.y - sinV * behind * r0);
        g.lineTo(this.x - cosV * behind * r1, this.y - sinV * behind * r1);
        g.stroke({ width: w, color: col, alpha: a * (1 - r0 * 0.7) });
      }
    }

    // Parallel speed-lines (fewer than local player)
    const maxLines = 18;
    while (this._remoteWarpLines.length < maxLines) {
      const lat = (Math.random() - 0.5) * 2;
      this._remoteWarpLines.push({
        pos: Math.random(),
        lateral: Math.abs(lat) < 0.15 ? (lat < 0 ? -0.2 : 0.2) : lat,
        speed: 1.0 + Math.random() * 1.5,
        len: 35 + Math.random() * 80,
        width: 0.5 + Math.random() * 1.5,
      });
    }

    for (const line of this._remoteWarpLines) {
      line.pos += line.speed * 0.018;
      if (line.pos > 1.15) {
        line.pos = -0.1;
        line.lateral = (Math.random() - 0.5) * 2;
        if (Math.abs(line.lateral) < 0.15) line.lateral = line.lateral < 0 ? -0.2 : 0.2;
        line.speed = 1.0 + Math.random() * 1.5;
      }
      if (line.pos < 0 || line.pos > 1) continue;

      const axisOffset = -ahead + line.pos * totalRange;
      const lat = line.lateral * spreadRadius;
      const halfLen = line.len / 2;
      const cx = this.x - cosV * axisOffset + cosP * lat;
      const cy = this.y - sinV * axisOffset + sinP * lat;

      const shipPos = ahead / totalRange;
      const dist = Math.abs(line.pos - shipPos);
      const lineAlpha = Math.max(0.05, 0.45 - dist * 0.8);
      const lineColor = Math.abs(line.lateral) < 0.3 ? 0xffffff : warpColor;

      g.moveTo(cx + cosV * halfLen, cy + sinV * halfLen);
      g.lineTo(cx - cosV * halfLen, cy - sinV * halfLen);
      g.stroke({ width: line.width, color: lineColor, alpha: Math.min(lineAlpha, 0.5) });
    }

    // Core glow at ship
    const coreR = 4 + Math.sin(t * 10) * 1.5;
    g.circle(this.x, this.y, coreR + 8);
    g.fill({ color: warpColor, alpha: 0.08 });
    g.circle(this.x, this.y, coreR + 3);
    g.fill({ color: warpColor, alpha: 0.2 });
    g.circle(this.x, this.y, coreR);
    g.fill({ color: 0xffffff, alpha: 0.65 });
  }

  /** Returns the current shield bubble radius if shield is active, or 0. */
  public getEffectiveShieldRadius(): number {
    if (!this.shieldActive) return 0;
    const sinceRaise = this.shieldRaisedAt ? Date.now() - this.shieldRaisedAt : this.shieldExpansionMs;
    const t = Math.min(1, sinceRaise / Math.max(1, this.shieldExpansionMs));
    return this.shieldRadius * t;
  }

  // TargetableEntity abstract method implementation
  public getSelectionInfo(): SelectionInfo {
    return {
      type: this.entityType,
      id: this.sessionId,
      name: this.username,
      x: this.x,
      y: this.y,
      data: {
        shipClass: this._visibleShipClassId,
        hull: this.hull,
        maxHull: this.maxHull,
        shield: this.shield,
        maxShield: this.maxShield,
        totalPower: this.totalPower,
      },
    };
  }

  override destroy(): void {
    this.warpTrailGraphics?.destroy();
    this._shieldBubbleGraphics?.destroy();
    this._remoteBeamGraphics?.destroy();
    super.destroy();
  }
}
